// Theme toggle — persists "light" | "dark" in localStorage. Default
// (no explicit choice yet) follows the OS via prefers-color-scheme,
// matching the modern norm (GitHub, Linear, etc.). A manual toggle
// writes to localStorage and takes precedence forever after.
// Applied via data-theme="light" on <html>, which overrides the :root CSS
// vars defined in styles/tokens.css. Also keeps the meta theme-color in
// sync so the mobile browser chrome matches.
//
// Wired by init() from bootstrap/legacy: call installTheme() once on boot.
// After that, refreshThemeLabel() can be called on locale change to keep
// the button tooltip in the active locale.

import { STORAGE_KEYS } from "../config/storage-keys";
import { t } from "../i18n";

export type Theme = "light" | "dark";

const LIGHT_COLOR = "#faf7f2";
const DARK_COLOR = "#16131a";

function loadTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEYS.THEME);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function applyTheme(theme: Theme): void {
  if (theme === "light") {
    document.documentElement.setAttribute("data-theme", "light");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
  const glyph = document.getElementById("themeToggleGlyph");
  if (glyph) {
    // Show the glyph of what clicking will switch TO.
    glyph.textContent = theme === "light" ? "☾" : "☀";
  }
  const btn = document.getElementById("themeToggleBtn");
  if (btn) {
    btn.title = t(theme === "light" ? "theme_to_dark" : "theme_to_light") as string;
  }
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", theme === "light" ? LIGHT_COLOR : DARK_COLOR);
  }
}

/**
 * Wire theme toggle button + apply persisted theme on boot. Call once.
 */
export function installTheme(): void {
  applyTheme(loadTheme());
  const btn = document.getElementById("themeToggleBtn");
  if (btn) {
    btn.addEventListener("click", () => {
      const next: Theme = loadTheme() === "light" ? "dark" : "light";
      localStorage.setItem(STORAGE_KEYS.THEME, next);
      applyTheme(next);
    });
  }
}

/** Refresh the button tooltip — call after locale changes. */
export function refreshThemeLabel(): void {
  const btn = document.getElementById("themeToggleBtn");
  if (!btn) return;
  const isLight = document.documentElement.getAttribute("data-theme") === "light";
  btn.title = t(isLight ? "theme_to_dark" : "theme_to_light") as string;
}
